import { useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { Archive, Settings, BookOpen, Menu, X, CloudLightning, Zap, Home, Sun, Moon } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_thunder-alert-maps/artifacts/6okjv5b7_UKWX%20LOGO%20NEW%202025.png";

export default function Layout() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("ukwx-theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("ukwx-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  
  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { to: "/", label: "Home", icon: Home, testId: "nav-home", exact: true },
    { to: "/outlook", label: "Outlook", icon: null, testId: "nav-outlook", exact: true },
    { to: "/archive", label: "Archive", icon: Archive, testId: "nav-archive" },
    { to: "/criteria", label: "Criteria", icon: BookOpen, testId: "nav-criteria" },
    { to: "/storms", label: "Named Storms", icon: CloudLightning, testId: "nav-storms" },
    { to: "/lai-v1", label: "LAI.v1", icon: Zap, testId: "nav-lai-v1" },
  ];

  const adminLink = { to: "/admin", label: "Admin", icon: Settings, testId: "nav-admin", muted: true };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--base-background)" }}>
      {/* Navigation */}
      <nav
        className="sticky top-0 z-50 w-full backdrop-blur-xl"
        style={{
          backgroundColor: "var(--nav-bg)",
          borderBottom: "1px solid var(--nav-border)",
          paddingTop: "env(safe-area-inset-top)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Synoptic isobar background — decorative only */}
        <svg
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
          viewBox="0 0 1440 64"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g stroke="#8ba5bb" fill="none" strokeWidth="0.8">
            <path d="M-20 14 C 280 8 600 22 870 15 S 1200 6 1460 14" opacity="0.07" />
            <path d="M-20 32 C 280 25 600 40 870 32 S 1200 23 1460 32" opacity="0.055" />
            <path d="M-20 52 C 280 44 600 59 870 51 S 1200 42 1460 51" opacity="0.04" />
          </g>
          <text x="1330" y="24" fill="#8ba5bb" opacity="0.06" fontSize="7" fontFamily="monospace" letterSpacing="1">1000</text>
        </svg>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <a 
              href="/" 
              className="flex items-center gap-3 group"
              data-testid="logo-link"
            >
              <img 
                src={LOGO_URL} 
                alt="UKWX Logo" 
                className="h-10 w-auto"
              />
              <div>
                <span className="font-heading text-lg font-bold tracking-wider uppercase" style={{ color: "var(--text-primary)" }}>
                  UKWX Outlooks
                </span>
              </div>
            </a>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map(({ to, label, icon: Icon, testId, exact }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors ${
                    isActive(to, exact)
                      ? "text-[#FBBF24]"
                      : "text-[#9CA3AF] hover:text-[#F9FAFB]"
                  }`}
                  data-testid={testId}
                >
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {label}
                </Link>
              ))}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="theme-toggle"
                data-testid="theme-toggle"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* Admin link */}
              <Link
                to={adminLink.to}
                className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors text-[#6B7280] hover:text-[#9CA3AF]"
                data-testid={adminLink.testId}
              >
                <Settings className="w-3.5 h-3.5" />
                {adminLink.label}
              </Link>
            </div>

            {/* Mobile: theme toggle + hamburger */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={toggleTheme}
                aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="theme-toggle"
                data-testid="theme-toggle-mobile"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <button
                className="flex items-center justify-center w-9 h-9 transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div
            className="md:hidden"
            style={{ borderTop: "1px solid var(--nav-border)", backgroundColor: "var(--base-background)" }}
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-2 max-w-7xl">
              {[...navLinks, adminLink].map(({ to, label, icon: Icon, testId, muted, exact }) => (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-3 px-2 py-3 text-xs font-medium uppercase tracking-[0.15em] transition-colors border-b last:border-0 ${
                    muted
                      ? "text-[#6B7280] hover:text-[#9CA3AF]"
                      : isActive(to, exact)
                      ? "text-[#FBBF24]"
                      : "text-[#9CA3AF] hover:text-[#F9FAFB]"
                  }`}
                  style={{ borderBottomColor: "var(--nav-border)" }}
                  data-testid={`mobile-${testId}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className="py-8 mt-16"
        style={{
          borderTop: "1px solid var(--nav-border)",
          paddingBottom: "calc(2rem + env(safe-area-inset-bottom))",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Synoptic isobar background — decorative only */}
        <svg
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
          viewBox="0 0 1440 130"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g stroke="#8ba5bb" fill="none" strokeWidth="0.8">
            <path d="M-20 28 C 300 20 700 42 1050 30 S 1300 16 1460 28" opacity="0.06" />
            <path d="M-20 65 C 300 56 700 79 1050 66 S 1300 52 1460 64" opacity="0.05" />
            <path d="M-20 102 C 300 93 700 115 1050 103 S 1300 89 1460 101" opacity="0.04" />
          </g>
          <text x="80" y="56" fill="#8ba5bb" opacity="0.05" fontSize="7" fontFamily="monospace" letterSpacing="1">996</text>
          <text x="1370" y="90" fill="#8ba5bb" opacity="0.04" fontSize="14" fontFamily="monospace">L</text>
        </svg>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
              UKWX Outlooks
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              Data for educational and informational purposes only
            </div>
          </div>
          <div className="flex justify-center mt-6">
            <a
              href="https://buymeacoffee.com/ukwx"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                alt="Buy Me A Coffee"
                className="h-10"
                width="160"
                height="40"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
